import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { cloneDeep, isEmpty, isFinite } from 'lodash';
import { toast } from 'material-react-toastify';
import { useRecoilValue } from 'recoil';
import { toastApiError } from '../../utils/ApiError';
import Waiting from '../Waiting';
import { sharedColors, sharedStyles } from '../../utils/Style';
import { Department } from '../../interfaces/Department';
import { deleteDepartment, upsertDepartment } from '../../services/Department';
import {
  getDepartmentPersonnelCountsTemplate,
  getDepartmentTemplate,
  validateDepartment,
} from '../../utils/Department';
import NumberInput from '../NumberInput';
import ConfirmDialog from '../ConfirmDialog';
import { Institution } from '../../interfaces/Institution';
import InstitutionDropdown from '../Institution/InstitutionDropdown';
import { tokenDataAtom } from '../../atoms/TokenData';
import { hasWriteAccess } from '../../utils/TokenData';
import { AccessLevel } from '../../interfaces/User';

interface UpsertDepartmentDialogProps {
  open: boolean;
  clickedDepartment?: Department;
  institutions: Institution[];
  onClose: () => void;
  onModify: () => void;
}

const UpsertDepartmentDialog = (props: UpsertDepartmentDialogProps) => {
  const { t } = useTranslation();

  const tokenData = useRecoilValue(tokenDataAtom);

  const [loading, setLoading] = useState(false);
  const [currentState, setCurrentState] = useState(getDepartmentTemplate());
  const [totalPersonnelCounts, setTotalPersonnelCounts] = useState(
    getDepartmentPersonnelCountsTemplate(),
  );
  const [clickedDelete, setClickedDelete] = useState(false);

  useEffect(() => {
    setCurrentState(
      props.clickedDepartment
        ? cloneDeep(props.clickedDepartment)
        : getDepartmentTemplate(),
    );

    if (!props.clickedDepartment) {
      setClickedDelete(false);
    }
  }, [props.clickedDepartment, props.open]);

  useEffect(() => {
    if (!props.clickedDepartment && !isEmpty(props.institutions)) {
      setCurrentState({
        ...currentState,
        institutionID: props.institutions[0].ID!,
        institutionName: props.institutions[0].name,
      });
    }
  }, [props.institutions]);

  useEffect(() => {
    setTotalPersonnelCounts({
      day:
        currentState.minimumPersonnelCounts.day +
        currentState.minimumSeniorPersonnelCounts.day,
      night:
        currentState.minimumPersonnelCounts.night +
        currentState.minimumSeniorPersonnelCounts.night,
      weekend:
        currentState.minimumPersonnelCounts.weekend +
        currentState.minimumSeniorPersonnelCounts.weekend,
    });
  }, [currentState]);

  const handleConfirm = () => {
    setLoading(true);

    upsertDepartment(currentState)
      .then(() => {
        toast.success(
          props.clickedDepartment
            ? t('department.successful.update')
            : t('department.successful.add'),
        );
        props.onModify();
      })
      .catch((err) => toastApiError(err))
      .finally(() => setLoading(false));
  };

  const handleDelete = () => {
    if (!isFinite(props.clickedDepartment?.ID)) {
      return;
    }

    setLoading(true);
    deleteDepartment(props.clickedDepartment!.ID!)
      .then(() => {
        toast.success(t('department.successful.delete'));
        setClickedDelete(false);
        props.onModify();
      })
      .catch((err) => toastApiError(err))
      .finally(() => setLoading(false));
  };

  const handleChangeName = (e: any) => {
    setCurrentState({ ...currentState, name: e.target.value });
  };

  const handleChangeInstitution = (newInstitution: Institution) => {
    setCurrentState({
      ...currentState,
      institutionID: newInstitution.ID!,
      institutionName: newInstitution.name,
    });
  };

  const handleChangeMinDayShiftPersonnelCountName = (newValue: number) => {
    setCurrentState({
      ...currentState,
      minimumPersonnelCounts: {
        ...currentState.minimumPersonnelCounts,
        day: newValue,
      },
    });
  };

  const handleChangeMinDayShiftSeniorPersonnelCountName = (
    newValue: number,
  ) => {
    setCurrentState({
      ...currentState,
      minimumSeniorPersonnelCounts: {
        ...currentState.minimumSeniorPersonnelCounts,
        day: newValue,
      },
    });
  };

  const handleChangeMinNightShiftPersonnelCountName = (newValue: number) => {
    setCurrentState({
      ...currentState,
      minimumPersonnelCounts: {
        ...currentState.minimumPersonnelCounts,
        night: newValue,
      },
    });
  };

  const handleChangeMinNightShiftSeniorPersonnelCountName = (
    newValue: number,
  ) => {
    setCurrentState({
      ...currentState,
      minimumSeniorPersonnelCounts: {
        ...currentState.minimumSeniorPersonnelCounts,
        night: newValue,
      },
    });
  };

  const handleChangeMinWeekendShiftPersonnelCountName = (newValue: number) => {
    setCurrentState({
      ...currentState,
      minimumPersonnelCounts: {
        ...currentState.minimumPersonnelCounts,
        weekend: newValue,
      },
    });
  };

  const handleChangeMinWeekendShiftSeniorPersonnelCountName = (
    newValue: number,
  ) => {
    setCurrentState({
      ...currentState,
      minimumSeniorPersonnelCounts: {
        ...currentState.minimumSeniorPersonnelCounts,
        weekend: newValue,
      },
    });
  };

  const personnelSections = [
    {
      shift: 'day' as const,
      onPersonnelChange: handleChangeMinDayShiftPersonnelCountName,
      onSeniorChange: handleChangeMinDayShiftSeniorPersonnelCountName,
    },
    {
      shift: 'night' as const,
      onPersonnelChange: handleChangeMinNightShiftPersonnelCountName,
      onSeniorChange: handleChangeMinNightShiftSeniorPersonnelCountName,
    },
    {
      shift: 'weekend' as const,
      onPersonnelChange: handleChangeMinWeekendShiftPersonnelCountName,
      onSeniorChange: handleChangeMinWeekendShiftSeniorPersonnelCountName,
    },
  ];

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth={false}
      aria-labelledby='department-dialog-title'
      PaperProps={{
        sx: {
          m: { xs: 2, lg: 4 },
          width: { xs: 'calc(100% - 32px)', lg: 650 },
          maxHeight: { xs: 'calc(100% - 32px)', lg: 'calc(100% - 64px)' },
        },
      }}
    >
      <ConfirmDialog
        content={t('department.delete.confirm_message')}
        open={clickedDelete}
        onClose={() => setClickedDelete(false)}
        onConfirm={handleDelete}
        color='error'
      />
      <Waiting open={loading} />
      <DialogTitle
        id='department-dialog-title'
        sx={{
          ...sharedStyles.h6,
          color: sharedColors.gray6,
          px: { xs: 2, lg: 3 },
        }}
      >
        {props.clickedDepartment
          ? t('department.dialog.update')
          : t('department.dialog.add')}
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          px: { xs: 2, lg: 3 },
        }}
      >
        <TextField
          label={t('department.name')}
          value={currentState.name}
          onChange={handleChangeName}
          size='small'
          error={isEmpty(currentState.name)}
          sx={{ mt: 1.5 }}
          fullWidth
        />
        <InstitutionDropdown
          label={t('department.institution') as string}
          backgroundColor={sharedColors.white}
          options={props.institutions}
          disabled={!!props.clickedDepartment}
          value={currentState.institutionID}
          onChange={handleChangeInstitution}
        />
        {personnelSections.map(
          ({ shift, onPersonnelChange, onSeniorChange }) => (
            <Box
              component='fieldset'
              key={shift}
              sx={{
                m: 0,
                p: 2,
                minWidth: 0,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography
                component='legend'
                sx={{ px: 0.5, fontSize: 14, fontWeight: 500 }}
              >
                {t(`department.personnel_count.${shift}`)}
                {shift === 'day' ? '*' : ''}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'minmax(0, 1fr)',
                    lg: 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                <NumberInput
                  label={t('department.min_personnel_count') as string}
                  value={currentState.minimumPersonnelCounts[shift]}
                  onChange={onPersonnelChange}
                  error={currentState.minimumPersonnelCounts[shift] < 0}
                />
                <NumberInput
                  label={t('department.min_senior_personnel_count') as string}
                  value={currentState.minimumSeniorPersonnelCounts[shift]}
                  onChange={onSeniorChange}
                  error={currentState.minimumSeniorPersonnelCounts[shift] < 0}
                />
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <NumberInput
                    label={t('department.min_total_personnel_count') as string}
                    value={totalPersonnelCounts[shift]}
                    disabled
                    error={totalPersonnelCounts[shift] < 0}
                  />
                </Box>
              </Box>
            </Box>
          ),
        )}
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {`* ${t('department.except_head_nurse')}`}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{ px: { xs: 2, lg: 2.5 }, pb: 1.5, flexWrap: 'wrap', rowGap: 1 }}
      >
        {hasWriteAccess(tokenData, AccessLevel.INSTITUTION) && (
          <Button
            variant='contained'
            color='error'
            disabled={!props.clickedDepartment}
            onClick={() => setClickedDelete(true)}
            sx={sharedStyles.buttonText}
          >
            {t('shared.delete')}
          </Button>
        )}
        <Box component='div' sx={{ display: 'flex', flexGrow: 1 }} />
        <Button
          variant='contained'
          color='inherit'
          onClick={props.onClose}
          sx={sharedStyles.buttonText}
        >
          {t('shared.cancel')}
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={!validateDepartment(currentState)}
          onClick={handleConfirm}
          sx={sharedStyles.buttonText}
        >
          {props.clickedDepartment
            ? t('department.dialog.update')
            : t('department.dialog.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpsertDepartmentDialog;
